import type { ReactNode } from 'react';

import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';


/**
 * ONE RANGE OF A `between` ANSWER — low, "to", high — shared by every block that has them.
 *
 * Five editors grew this row independently (Numerical, Slider, Compass, Tilt, Rotation) and
 * had it identical to the character, which is the shape that drifts the moment one of them is
 * touched. The owner, after the first one was restyled: "apply the same to any editor where
 * you can set up betweens so that stylistically it is the same".
 *
 * ## The two bounds STACK, always
 *
 * Side by side they share the 448px answer column and take about 190px each, and a bound is a
 * FORMULA — a chip, an operator and a second chip fill that before the teacher has finished
 * the expression. Reported as "for equations, they're really squished if it's all in one
 * line". Always, not once they grow long: "hmm ... i think i'd rather the layout not shift all
 * of a sudden" — a row that re-flows mid-keystroke moves the caret's neighbours while someone
 * is reading them.
 *
 * The connector is centred between them, so the eye reads one quantity split across two boxes
 * rather than two boxes with a word stuck to the left of the second.
 *
 * ## A border only when there are several
 *
 * Stacked, two ranges are four boxes in a column and nothing says where one ends. The border
 * groups each set. A single range needs none — there is nothing to tell it apart FROM, and an
 * outline around the only thing on screen is a frame for its own sake.
 */
export function BetweenAnswerRow({ fieldId, armed, grouped, to, low, high, action, children }: {
  /** The range's own id, so a reported issue can scroll to it — `between-incomplete` does. */
  fieldId: string;
  armed: boolean;
  /** More than one range in the set, which is the only reason to draw a border. */
  grouped: boolean;
  /** The lower bound's field, composed by the caller — Compass hangs a cardinal hint beside its own. */
  low: ReactNode;
  high: ReactNode;
  /** The delete control, centred against the whole stack because it removes the RANGE. */
  action?: ReactNode;
  /** Anything that belongs under the pair, such as Compass's wedge diagram. */
  children?: ReactNode;
  /** The connector's word, which every caller reads from the same key. */
  to: string;
}) {
  return (
    <div data-field-id={fieldId} className='flex flex-col gap-1'>
      <div className={`flex items-center gap-2 ${
        grouped ? 'rounded-lg border border-ws-border p-2' : ''} ${
        armed ? `rounded-lg ${ARMED_DELETE_PAINT} ${grouped ? '' : ARMED_DELETE_BLEED}` : ''}`}>
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          {low}
          {/* Aria-hidden: each field names its own bound, and a reader meeting "to" between two
              labelled boxes is told the same thing twice. */}
          <span aria-hidden='true' className='select-none self-center text-sm text-ws-muted'>{to}</span>
          {high}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
